/**
 * Demo animada de la landing pública — TALAPP-1916.
 *
 * Un teléfono con la app de AVA recorre 4 escenas en loop (identidad,
 * documentos, revisión de datos, solicitud enviada). JS solo decide qué
 * escena está activa; las transiciones y el movimiento son keyframes CSS de
 * `landingDemo.css`.
 *
 * - El teléfono se escala para entrar en el alto disponible (la landing es una
 *   sola pantalla sin scroll): `--ld-scale` se calcula con un ResizeObserver.
 * - La demo es decorativa (`aria-hidden`); el paso actual se anuncia como
 *   texto (sin aria-live: cambia solo cada 4,5 s) y la pausa es un botón real.
 * - Con `prefers-reduced-motion` arranca pausada y sin movimiento.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, Check, FileText, Mail, Pause, Play } from "lucide-react";
import { LogoMark } from "@ava/shared/components/design-system";
import { LANDING_DEMO } from "@/pages/home/landingContent";
import "@/pages/home/landingDemo.css";

const SCENE_MS = 4500;
const SCENE_COUNT = LANDING_DEMO.captions.length;
const PHONE_HEIGHT = 610;
const MIN_SCALE = 0.25;
/** Alto que se reserva debajo del teléfono para el caption (desktop). */
const CAPTION_SPACE = 48;
/** Ancho que se reserva a cada lado para la pausa (gap + 44 px). */
const SIDE_SPACE_DESKTOP = 48;
const SIDE_SPACE_MOBILE = 56;
const DESKTOP_QUERY = "(min-width: 768px)";

// Escalonado de cada pieza (clases `ld-d*` de landingDemo.css).
const DOC_DELAYS = ["ld-d320", "ld-d440", "ld-d560"];
const FIELD_DELAYS = ["ld-d500", "ld-d1100", "ld-d1700", "ld-d2300"];
const TICK_DELAYS = ["ld-d880", "ld-d1480", "ld-d2080", "ld-d2680"];

const TONE_CLASS = {
  success: "ld-tone-success",
  info: "ld-tone-info",
  warn: "ld-tone-warn",
} as const;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

export function LandingDemo() {
  const [startsPaused] = useState(prefersReducedMotion);
  const [{ scene, prev }, setScenes] = useState({ scene: 1, prev: 0 });
  const [playing, setPlaying] = useState(!startsPaused);
  const stageRef = useRef<HTMLDivElement>(null);
  const remainingRef = useRef(SCENE_MS);
  const startedAtRef = useRef(0);

  // Avance de escena. Al pausar se guarda el tiempo que faltaba, así al
  // reanudar sigue en el mismo punto que las animaciones CSS (pausadas).
  useEffect(() => {
    if (!playing) return;
    startedAtRef.current = Date.now();
    const timer = window.setTimeout(() => {
      remainingRef.current = SCENE_MS;
      setScenes((current) => ({ prev: current.scene, scene: (current.scene % SCENE_COUNT) + 1 }));
    }, remainingRef.current);
    return () => window.clearTimeout(timer);
  }, [playing, scene]);

  // Escala del teléfono según el espacio disponible.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const update = () => {
      const baseWidth = parseFloat(getComputedStyle(stage).getPropertyValue("--ld-w")) || 292;
      const desktop = window.matchMedia(DESKTOP_QUERY).matches;
      const { width, height } = stage.getBoundingClientRect();
      const byHeight = (height - (desktop ? CAPTION_SPACE : 0)) / PHONE_HEIGHT;
      const byWidth = (width - 2 * (desktop ? SIDE_SPACE_DESKTOP : SIDE_SPACE_MOBILE)) / baseWidth;
      const scale = Math.max(MIN_SCALE, Math.min(1, byHeight, byWidth));
      stage.style.setProperty("--ld-scale", scale.toFixed(3));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const togglePlaying = () => {
    if (playing) {
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    }
    setPlaying(!playing);
  };

  const sceneClass = (n: number) => `ld-scene${n === scene ? " is-on" : n === prev ? " is-out" : ""}`;
  const swapClass = (n: number) => `ld-swap${n === scene ? " is-on" : ""}`;
  const caption = LANDING_DEMO.captions[scene - 1];
  const { identity, documents, review, sent } = LANDING_DEMO;

  return (
    <section aria-label={LANDING_DEMO.regionLabel} className="relative min-h-40 flex-1 md:min-h-0 md:self-stretch">
      <div
        ref={stageRef}
        className={`ld-stage absolute inset-0 flex flex-col items-center justify-center gap-3.5${playing ? "" : " is-paused"}`}
      >
        <p className="sr-only">{caption}</p>

        <div className="ld-frame">
          <div className="ld-phone" aria-hidden="true">
            <div className="ld-screen">
              <div className="ld-island" />

              <div className="ld-appbar">
                <LogoMark size={22} />
                <span className="ld-appbar-brand">
                  <span className="ld-appbar-name">AVA</span>
                  <span className="ld-appbar-step">
                    {LANDING_DEMO.captions.map((_, i) => (
                      <span key={i} className={swapClass(i + 1)}>
                        {LANDING_DEMO.stepLabel(i + 1, SCENE_COUNT)}
                      </span>
                    ))}
                  </span>
                </span>
              </div>
              <div className="ld-track">
                <div className={`ld-prog ${scene % 2 ? "is-a" : "is-b"}`} />
              </div>

              <div className="ld-viewport">
                {/* 1 — Frente del DNI */}
                <div className={sceneClass(1)}>
                  <div className="ld-capture">
                    <div className="ld-viewfinder">
                      <span className="ld-corner ld-corner-tl" />
                      <span className="ld-corner ld-corner-tr" />
                      <span className="ld-corner ld-corner-bl" />
                      <span className="ld-corner ld-corner-br" />
                      <div className="ld-card">
                        <div className="ld-card-photo" />
                        <div className="ld-card-lines">
                          <div className="ld-line w-[62%]" />
                          <div className="ld-line w-[88%]" />
                          <div className="ld-line w-1/2" />
                          <div className="ld-line w-[76%]" />
                        </div>
                        <div className="ld-line ld-card-foot" />
                        <div className="ld-scan" />
                      </div>
                    </div>
                    <div className="ld-stack">
                      <span className="ld-badge ld-badge-lg ld-tone-info ld-wait">
                        <Camera size={13} />
                        {identity.framing}
                      </span>
                      <span className="ld-badge ld-badge-lg ld-tone-success ld-rise ld-d2700">
                        <Check size={13} strokeWidth={2.5} />
                        {identity.captured}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2 — Documentos */}
                <div className={sceneClass(2)}>
                  <div className="ld-col">
                    <span className="ld-title ld-rise ld-d220 pb-2.5">{documents.title}</span>
                    {documents.items.map((doc, i) => (
                      <div key={doc.name} className={`ld-doc ld-rise ${DOC_DELAYS[i]}`}>
                        <span className="ld-doc-icon">
                          <FileText size={15} />
                        </span>
                        <span className="ld-doc-name">
                          {doc.name}
                          {doc.tone === "info" && (
                            <span className="ld-bar-track">
                              <span className="ld-bar" />
                            </span>
                          )}
                        </span>
                        <span className={`ld-badge ${TONE_CLASS[doc.tone]}`}>{doc.status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3 — Datos leídos de los documentos */}
                <div className={sceneClass(3)}>
                  <div className="ld-col">
                    <div className="ld-review-head">
                      <span className="ld-title">{review.title}</span>
                      <span className="ld-badge ld-tone-info">{review.badge}</span>
                    </div>
                    {review.fields.map((field, i) => (
                      <div key={field.label} className="ld-row">
                        <span className="ld-row-text">
                          <span className="ld-row-label">{field.label}</span>
                          <span className={`ld-row-value${field.mono ? " is-mono" : ""}`}>
                            <span className={`ld-type ${FIELD_DELAYS[i]}`}>{field.value}</span>
                          </span>
                        </span>
                        <span className={`ld-tick ${TICK_DELAYS[i]}`}>
                          <Check size={12} strokeWidth={2.5} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4 — Solicitud enviada (sin prometer aprobación) */}
                <div className={sceneClass(4)}>
                  <div className="ld-sent">
                    <span className="ld-sent-icon ld-rise ld-d240">
                      <Mail size={24} />
                    </span>
                    <span className="ld-sent-title ld-rise ld-d380">{sent.title}</span>
                    <span className="ld-sent-body ld-rise ld-d500">{sent.body}</span>
                    <span className="ld-badge ld-badge-lg ld-tone-info ld-rise ld-d680">{sent.status}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="ld-pause"
            onClick={togglePlaying}
            aria-label={playing ? LANDING_DEMO.pauseLabel : LANDING_DEMO.playLabel}
          >
            <span className="ld-pause-dot">
              {playing ? <Pause size={12} aria-hidden="true" /> : <Play size={12} aria-hidden="true" />}
            </span>
          </button>
        </div>

        <div aria-hidden="true" className="hidden text-center text-sm font-medium leading-snug text-white/90 md:grid">
          {LANDING_DEMO.captions.map((text, i) => (
            <span key={text} className={swapClass(i + 1)}>
              {text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
